import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CommonBanner, Footer, Header } from "../../components";
import { HTTPURL } from "../../constants/Matcher";
import { spellOut } from "../../constants/catalogue";
import { C, FONT, sectionTitle } from "../../constants/mq";

// Pricing.
//
// Rates come straight from the fee schedules in the admin, so this page can
// never drift from what a family is actually charged.
//
// Discounts are deliberately absent. Whether Early Bird, sibling or the third
// subject applies depends on when you pay, who else is enrolled and how many
// subjects you take — a price list cannot answer that honestly, so we say the
// discounts exist and let staff work out the real number at enrolment.

// White table on white, sand header row, hairline rules; the fee notes are
// white cards on a sand band and the closing call to action is a sand box.
const s = {
  body: { background: "#ffffff", padding: "56px 0 40px" },
  container: { maxWidth: 1072, margin: "0 auto", padding: "0 24px" },
  lead: {
    fontSize: 16,
    lineHeight: 1.75,
    color: C.body,
    margin: "0 0 8px",
    maxWidth: 760,
  },
  note: { fontSize: 13.5, color: C.muted, margin: "0 0 36px", maxWidth: 760 },

  yearBlock: { marginBottom: 34 },
  yearHead: {
    display: "flex",
    alignItems: "baseline",
    gap: 12,
    margin: "0 0 12px",
  },
  yearTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: C.ink,
    margin: 0,
    letterSpacing: "-0.3px",
  },
  tableWrap: {
    background: "#ffffff",
    border: `1px solid ${C.line}`,
    borderRadius: 4,
    overflowX: "auto",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14.5, minWidth: 520 },
  th: {
    textAlign: "left",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    color: C.ink,
    background: C.sand,
    padding: "12px 18px",
    borderBottom: `1px solid ${C.line}`,
    whiteSpace: "nowrap",
  },
  thNum: { textAlign: "right" },
  td: {
    padding: "14px 18px",
    borderBottom: `1px solid ${C.line}`,
    color: C.body,
  },
  tdName: { fontWeight: 600, color: C.ink },
  nameNote: { display: "block", fontSize: 12.5, fontWeight: 400, color: C.muted, marginTop: 2 },
  tdNum: { textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  dash: { color: C.muted },

  infoBand: { background: C.sand, padding: "64px 0 72px" },
  h2: { ...sectionTitle, fontSize: 30, margin: "0 0 28px" },
  points: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 },
  point: { padding: "22px 22px 24px" },
  pointTitle: { fontSize: 15, fontWeight: 700, color: C.ink, margin: "0 0 6px" },
  pointBody: { fontSize: 14, lineHeight: 1.7, color: C.body, margin: 0 },

  ctaBand: { background: "#ffffff", padding: "56px 0 72px" },
  ctaBox: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 20,
    padding: 26,
  },
  ctaTitle: { fontSize: 19, fontWeight: 700, color: C.ink, margin: "0 0 4px", lineHeight: 1.4 },
  ctaBody: { fontSize: 13.5, color: C.muted, margin: 0, lineHeight: 1.55 },
  ctaRow: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 24 },
  empty: {
    border: `1px dashed ${C.line}`,
    borderRadius: 4,
    padding: "36px 20px",
    textAlign: "center",
    fontSize: 14,
    color: C.muted,
    background: C.sand,
  },
};

// the wrapper already draws the bottom edge, so the last row's rule is dropped
const tableCss = `
.cs-fee-table tbody tr:last-child td { border-bottom: none !important; }
`;

const money = (n) => (Number(n) > 0 ? `$${Number(n).toFixed(2)}` : null);

const points = [
  {
    title: "Tuition is charged per lesson",
    body: "You pay for the number of lessons that actually run in the term. A Saturday class may run 11 weeks in the same term a weekday class runs 10.",
  },
  {
    title: "Joining partway through",
    body: "If you start after the term begins, you pay only for the remaining lessons.",
  },
  {
    title: "Discounts",
    body: "Early Bird, sibling and third-subject discounts are available. Which one applies depends on your enrolment, so we work it out with you.",
  },
  {
    title: "All prices include GST",
    body: "Material fees are charged per term and are not discounted. Card payments carry a 1% surcharge.",
  },
];

const banner = {
  bannerHeading: "Pricing",
  subheading:
    "What each program costs per lesson, and the material fee for the term.",
  path: [{ link: "/pricing", name: "Pricing" }],
};

const Pricing = () => {
  const [years, setYears] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`${HTTPURL}api/public/fees`)
      .then((r) => r.json())
      .then((j) => {
        if (alive) setYears(Array.isArray(j?.data) ? j.data : []);
      })
      .catch(() => {})
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div style={{ background: "#fff", fontFamily: FONT }}>
      <style>{tableCss}</style>
      <Header />
      <CommonBanner {...banner} />

      <main>
        <section style={s.body}>
          <div style={s.container}>
            <p style={s.lead}>
              Tuition is charged per lesson, so the total for a term depends on
              how many lessons that class runs. Material fees cover the course
              books and are charged once per term.
            </p>
            <p style={s.note}>
              All prices include GST. Rates below are for our New South Wales
              campuses.
            </p>

            {!loaded ? null : years.length === 0 ? (
              <div style={s.empty}>
                Fees for the coming term are being finalised. Contact us and we
                will send you the current rates.
              </div>
            ) : (
              years.map((y) => (
                <div key={y.year} style={s.yearBlock}>
                  <div style={s.yearHead}>
                    <h2 style={s.yearTitle}>Year {y.year}</h2>
                  </div>
                  <div style={s.tableWrap}>
                    <table className="cs-fee-table" style={s.table}>
                      <thead>
                        <tr>
                          <th style={s.th}>Course</th>
                          <th style={{ ...s.th, ...s.thNum }}>Per lesson</th>
                          <th style={{ ...s.th, ...s.thNum }}>Material / term</th>
                        </tr>
                      </thead>
                      <tbody>
                        {y.courses.map((c) => (
                          <tr key={c.course_id}>
                            <td style={{ ...s.td, ...s.tdName }}>
                              {c.name}
                              {spellOut(c.name) && (
                                <span style={s.nameNote}>{spellOut(c.name)}</span>
                              )}
                            </td>
                            <td style={{ ...s.td, ...s.tdNum }}>
                              {money(c.price_per_lesson) || (
                                <span style={s.dash}>&mdash;</span>
                              )}
                            </td>
                            <td style={{ ...s.td, ...s.tdNum }}>
                              {money(c.material_fee_per_term) || (
                                <span style={s.dash}>&mdash;</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        <section style={s.infoBand}>
          <div style={s.container}>
            <h2 style={s.h2}>How fees work</h2>
            <div style={s.points}>
              {points.map((p) => (
                <div key={p.title} className="mq-card" style={s.point}>
                  <h3 style={s.pointTitle}>{p.title}</h3>
                  <p style={s.pointBody}>{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section style={s.ctaBand}>
          <div style={s.container}>
            <div className="mq-sand" style={s.ctaBox}>
              <div>
                <p style={s.ctaTitle}>Want the exact figure for your child?</p>
                <p style={s.ctaBody}>
                  Tell us the year level and campus and we will work out the term
                  total, discounts included.
                </p>
              </div>
              <div style={s.ctaRow}>
                <Link className="mq-btn" to="/contact-us">
                  Ask for a quote
                </Link>
                <Link className="mq-link" to="/enroll">
                  Enrol now
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Pricing;
